import InfoIcon from 'assets/icons/landing/info.svg?component'
import BodyText from 'components/UI/Landing/body-text'
import SubTitle from 'components/UI/Landing/subtitle'
import PrimaryText from 'components/UI/Landing/text-primary'
import { Funds } from 'components/views/Landing/section-terms.view'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import tw, { TwStyle } from 'twin.macro'
import { gradiendBorderStyles } from 'utils/constants'

type DamuLoanTypes = 'invest' | 'replenishment'
type FreedomLoanTypes = 'deposit' | 'nodeposit'

type TermsCardWithTabsProps = {
  fund: Funds
  twStyle?: TwStyle | TwStyle[]
  onFundChange: (fund: Funds) => void
}

const getLoanAmount = (type: DamuLoanTypes | FreedomLoanTypes) => {
  switch (type) {
    case 'deposit':
      return '100 000 000'
    case 'nodeposit':
      return '10 000 000'
    case 'invest':
      return '20 000 000'
    case 'replenishment':
      return '5 000 000'
  }
}

const getLoanPeriod = (type: DamuLoanTypes | FreedomLoanTypes) => {
  switch (type) {
    case 'deposit':
    case 'nodeposit':
      return '60'
    case 'invest':
      return '36'
    case 'replenishment':
      return '24'
  }
}

export default function TermsCardWithTabs({ fund, twStyle }: TermsCardWithTabsProps) {
  // Tools
  const { t } = useTranslation('landing')

  const isDamu = fund === 'damu'

  // State
  const [activeLoanType, setActiveLoanType] = useState<DamuLoanTypes | FreedomLoanTypes>(isDamu ? 'invest' : 'deposit')

  const isFirst = activeLoanType === 'invest' || activeLoanType === 'deposit'
  const isSecond = activeLoanType === 'replenishment' || activeLoanType === 'nodeposit'

  return (
    <div
      css={[
        tw`flex flex-col rounded-[20px] p-4 md:p-6 bg-[var(--bg-landing-card)] sm:justify-between shadow-sm z-10 relative`,
        twStyle,
        gradiendBorderStyles
      ]}
    >
      <div className={`rounded-xl p-1 bg-[var(--bg-landing-toggle)] mb-4 lg:mb-8 text-main flex gap-2 z-10`}>
        <button
          type='button'
          className={`w-full py-[15px] px-4 text-[13px] font-semibold rounded-xl ${
            isFirst ? 'bg-[var(--bg-landing-toggle-button)]' : ''
          }`}
          onClick={() => setActiveLoanType(isDamu ? 'invest' : 'deposit')}
        >
          {isDamu ? t('loan-terms.terms-card.tabs.investments') : t('loan-terms.terms-card.tabs.with-pledge')}
        </button>
        <button
          type='button'
          className={`w-full py-[15px] px-0 md:px-4 text-[13px] font-semibold rounded-xl ${
            isSecond ? 'bg-[var(--bg-landing-toggle-button)]' : ''
          }`}
          onClick={() => setActiveLoanType(isDamu ? 'replenishment' : 'nodeposit')}
        >
          {isDamu ? t('loan-terms.terms-card.tabs.replenishment') : t('loan-terms.terms-card.tabs.without-pledge')}
        </button>
      </div>
      <div className='flex flex-col gap-y-4'>
        <div>
          <SubTitle title={t('loan-terms.terms-card.loan-amount-text')} className='text-sm mb-1 lg:mb-2' />
          <PrimaryText
            text={t('loan-terms.terms-card.loan-amount-limit', {
              from: '500 000',
              to: getLoanAmount(activeLoanType)
            })}
            className='sm:text-lg text-[var(--text-landing-green)] text-[18px] leading-[140%]'
          />
        </div>
        <div>
          <SubTitle title={t('loan-terms.terms-card.period-text')} className={`text-sm mb-1 lg:mb-2`} />
          <PrimaryText
            text={t('loan-terms.terms-card.period-limit', {
              from: '6',
              to: getLoanPeriod(activeLoanType)
            })}
            className='text-[18px] leading-[140%]  sm:text-lg text-[var(--text-landing-green)]'
          />
        </div>
      </div>
      {fund === 'damu' && (
        <div className='flex items-center gap-3 p-3 mt-4 lg:mt-5 rounded-lg text-muted bg-[var(--bg-landing-warning)]'>
          <InfoIcon />
          <BodyText
            text={t('loan-terms.terms-card.damu-member-limit', { amount: '20 000 000' })}
            className='text-[12px] leading-[140%]'
          />
        </div>
      )}
    </div>
  )
}
